import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Dice from './Dice';
import propertiesData from '../data/properties.json';
import { Gavel, AlertTriangle, HelpCircle, Gift, Share2, Sparkles } from 'lucide-react';
import { TokenIcon } from './TokenIcon';
import { gameBrand } from '../config/brand';
import StoryCardModal from './StoryCardModal';
import { useLanguage } from '../i18n/LanguageContext';
import istanbulCenterImg from '../assets/cities/istanbul_center.jpg';
import ankaraCenterImg from '../assets/cities/ankara_center.jpg';
import izmirCenterImg from '../assets/cities/izmir_center.jpg';

export default function BoardCenter({
  gameData,
  room,
  isRolling,
  lastDice,
  myPlayerId,
  isMyTurn,
  sendCommand,
  activePlayer,
  me,
  isMyBidTurn,
  isMyDebt,
  auctionBid,
  setAuctionBid,
  selectedPropertyId,
  setSelectedPropertyId,
  displayedCard,
  cardRevealTimerActive,
  autoRoll,
  autoBuy,
  showOthersCards = true,
  navigateDeed,
  onOpenPlayers,
  onOpenChat,
  unreadChatCount,
  latestLog,
  logVisible
}) {
  const { lang, t } = useLanguage();
  const cityId = gameData?.settings?.city || room?.settings?.city || 'izmir';
  const cityBrand = gameBrand.getCityBrand(cityId, lang);
  const cityCenterImages = {
    istanbul: istanbulCenterImg,
    ankara: ankaraCenterImg,
    izmir: izmirCenterImg
  };
  const activeCenterImg = cityCenterImages[cityId] || izmirCenterImg;

  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showTradesModal, setShowTradesModal] = useState(false);
  const [tradesTab, setTradesTab] = useState('incoming');
  const [isProposingTrade, setIsProposingTrade] = useState(false);
  const [tradeReceiverId, setTradeReceiverId] = useState('');
  const [tradeGiveMoney, setTradeGiveMoney] = useState(0);
  const [tradeReceiveMoney, setTradeReceiveMoney] = useState(0);
  const [tradeGiveProps, setTradeGiveProps] = useState([]);
  const [tradeReceiveProps, setTradeReceiveProps] = useState([]);
  const [tradeGiveJailCardsCount, setTradeGiveJailCardsCount] = useState(0);
  const [tradeReceiveJailCardsCount, setTradeReceiveJailCardsCount] = useState(0);

  const [counteringTradeId, setCounteringTradeId] = useState(null);
  const [isTradePeekMode, setIsTradePeekMode] = useState(false);

  const handleCounterOffer = (trade) => {
    setTradeReceiverId(trade.senderId);
    setTradeReceiveMoney(trade.giveMoney || 0);
    setTradeReceiveProps(trade.giveProperties || []);
    setTradeReceiveJailCardsCount(trade.giveJailCards || 0);

    setTradeGiveMoney(trade.receiveMoney || 0);
    setTradeGiveProps(trade.receiveProperties || []);
    setTradeGiveJailCardsCount(trade.receiveJailCards || 0);

    setCounteringTradeId(trade.id);
    setShowTradesModal(false);
    setIsProposingTrade(true);
  };

  React.useEffect(() => {
    const invalidPhases = ['LOBBY', 'GAME_SETUP', 'ORDER_ROLL', 'GAME_OVER'];
    if (gameData) {
      if (invalidPhases.includes(gameData.phase) || gameData.activePlayerId !== myPlayerId) {
        setIsProposingTrade(false);
        setTradeReceiverId('');
        setTradeGiveMoney(0);
        setTradeReceiveMoney(0);
        setTradeGiveProps([]);
        setTradeReceiveProps([]);
        setTradeGiveJailCardsCount(0);
        setTradeReceiveJailCardsCount(0);
        setCounteringTradeId(null);
      }
    }
  }, [gameData?.phase, gameData?.activePlayerId, myPlayerId]);

  if (!gameData) return null;

  // Helper to fetch board property data
  const getPropertyDetail = (id) => {
    return propertiesData.find(p => p.id === id);
  };

  // Helper to check color group ownership
  const playerOwnsFullGroup = (playerId, color) => {
    if (!color) return false;
    const groupTiles = propertiesData.filter(p => p.type === 'property' && p.color === color);
    return groupTiles.every(t => gameData.properties[t.id]?.ownerPlayerId === playerId);
  };

  // Resolve which card to show (only during active CARD_REVEAL phase)
  const isMyTurnForCard = gameData.currentPlayerId === myPlayerId;
  const showDrawnCard = gameData.phase === 'CARD_REVEAL' && gameData.currentCard && (isMyTurnForCard || showOthersCards);
  const cardToRender = gameData.currentCard;

  // ====================================================
  // OVERLAY RENDERERS (FULL CENTER WIDTH)
  // ====================================================

  const renderTradeBuilder = () => {
    const mePlayer = me || gameData.players.find(p => p.playerId === myPlayerId);
    const receiverPlayer = gameData.players.find(p => p.playerId === tradeReceiverId);

    return createPortal(
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001, display: 'flex', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box' }}>
        <div 
          className="deed-backdrop-overlay" 
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => {
            setIsProposingTrade(false);
            sendCommand('CANCEL_TRADE_PROPOSAL');
          }}
        />
        <div className="stage-trade-builder-overlay animate-card-fly" style={{ zIndex: 10002, position: 'relative', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
          <button className="deed-close-btn" onClick={() => {
            setIsProposingTrade(false);
            sendCommand('CANCEL_TRADE_PROPOSAL');
          }}>✕</button>
        <div className="trade-builder-card">
          <h3 className="trade-builder-title">
            {counteringTradeId ? t('trades.counter_title', '🔄 Karşı Teklif Oluşturuluyor') : t('trades.title', '🤝 TAKAS TEKLİFİ OLUŞTUR')}
          </h3>
          
          <div className="trade-builder-row target-select-row" style={{ marginBottom: '16px' }}>
            <label className="field-label" style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: '#94a3b8', fontWeight: 600 }}>Kiminle Takas Yapacaksınız?</label>
            {!tradeReceiverId ? (
              <div className="trade-target-grid" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {gameData.players
                  .filter(p => p.playerId !== myPlayerId && !p.bankrupt)
                  .map(p => (
                    <button 
                      key={p.playerId}
                      type="button"
                      className="trade-target-btn"
                      onClick={() => {
                        setTradeReceiverId(p.playerId);
                        setTradeReceiveProps([]);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: `1.5px solid ${p.color}40`,
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'}
                    >
                      <TokenIcon token={p.token} color={p.color} size={24} />
                      <span style={{ color: p.color, fontWeight: 'bold', fontSize: '0.9rem' }}>{p.name}</span>
                    </button>
                  ))}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.03)', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TokenIcon token={receiverPlayer?.token} color={receiverPlayer?.color} size={24} />
                  <span style={{ fontWeight: 800, color: receiverPlayer?.color, fontSize: '0.95rem' }}>{receiverPlayer?.name}</span>
                </div>
                <button 
                  type="button" 
                  onClick={() => {
                    setTradeReceiverId('');
                    setTradeReceiveProps([]);
                  }}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                >
                  Değiştir
                </button>
              </div>
            )}
          </div>

          {tradeReceiverId && (
            <div className="trade-deal-builder-columns">
              <div className="trade-builder-col">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                  <TokenIcon token={mePlayer?.token} color={mePlayer?.color} size={18} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 800, color: mePlayer?.color }}>{mePlayer?.name} (Siz)</span>
                </div>
                <div className="money-input-field">
                  <label>Nakit Para (Mevcut: ₺{mePlayer?.money}):</label>
                  <input 
                    type="number"
                    min="0"
                    max={mePlayer?.money || 0}
                    value={tradeGiveMoney === 0 ? '' : tradeGiveMoney}
                    onChange={(e) => setTradeGiveMoney(Math.min(mePlayer?.money || 0, Math.max(0, parseInt(e.target.value) || 0)))}
                    className="trade-money-input"
                  />
                </div>
                <div className="properties-checkbox-list">
                  <label>Mülkleriniz:</label>
                  <div className="trade-props-scroll">
                    {propertiesData
                      .filter(pd => gameData.properties[pd.id]?.ownerPlayerId === myPlayerId)
                      .map(prop => (
                        <label key={prop.id} className="trade-prop-checkbox-item">
                          <input 
                            type="checkbox"
                            checked={tradeGiveProps.includes(prop.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTradeGiveProps([...tradeGiveProps, prop.id]);
                              } else {
                                setTradeGiveProps(tradeGiveProps.filter(id => id !== prop.id));
                              }
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <span className="prop-bullet" style={{ background: prop.color || '#475569' }}></span>
                          <span className="prop-name-lbl">
                            {prop.name} {gameData.properties[prop.id]?.mortgaged && ' (🔒 İpotekli)'}
                          </span>
                        </label>
                      ))}
                    {mePlayer?.jailFreeCards && mePlayer.jailFreeCards.map((card, cIdx) => (
                      <label key={`trade-jailfree-give-${cIdx}`} className="trade-prop-checkbox-item">
                        <input 
                          type="checkbox"
                          checked={tradeGiveJailCardsCount > cIdx}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTradeGiveJailCardsCount(cIdx + 1);
                            } else {
                              setTradeGiveJailCardsCount(cIdx);
                            }
                          }}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="prop-bullet" style={{ background: '#eab308' }}></span>
                        <span className="prop-name-lbl">🔑 Tahliye Kartı ({card.isChance ? 'Şans' : 'Kamu'})</span>
                      </label>
                    ))}
                    {propertiesData.filter(pd => gameData.properties[pd.id]?.ownerPlayerId === myPlayerId).length === 0 && (!mePlayer?.jailFreeCards || mePlayer.jailFreeCards.length === 0) && (
                      <div className="no-props-txt">Takas edilebilir mülkünüz yok.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="trade-builder-col">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                  <TokenIcon token={receiverPlayer?.token} color={receiverPlayer?.color} size={18} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 800, color: receiverPlayer?.color }}>{receiverPlayer?.name}</span>
                </div>
                <div className="money-input-field">
                  <label>Nakit Para (Mevcut: ₺{receiverPlayer?.money}):</label>
                  <input 
                    type="number"
                    min="0"
                    max={receiverPlayer?.money || 0}
                    value={tradeReceiveMoney === 0 ? '' : tradeReceiveMoney}
                    onChange={(e) => setTradeReceiveMoney(Math.min(receiverPlayer?.money || 0, Math.max(0, parseInt(e.target.value) || 0)))}
                    className="trade-money-input"
                  />
                </div>
                <div className="properties-checkbox-list">
                  <label>Mülkleri:</label>
                  <div className="trade-props-scroll">
                    {propertiesData
                      .filter(pd => gameData.properties[pd.id]?.ownerPlayerId === tradeReceiverId)
                      .map(prop => (
                        <label key={prop.id} className="trade-prop-checkbox-item">
                          <input 
                            type="checkbox"
                            checked={tradeReceiveProps.includes(prop.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTradeReceiveProps([...tradeReceiveProps, prop.id]);
                              } else {
                                setTradeReceiveProps(tradeReceiveProps.filter(id => id !== prop.id));
                              }
                            }}
                          />
                          <span className="checkbox-custom"></span>
                          <span className="prop-bullet" style={{ background: prop.color || '#475569' }}></span>
                          <span className="prop-name-lbl">
                            {prop.name} {gameData.properties[prop.id]?.mortgaged && ' (🔒 İpotekli)'}
                          </span>
                        </label>
                      ))}
                    {receiverPlayer?.jailFreeCards && receiverPlayer.jailFreeCards.map((card, cIdx) => (
                      <label key={`trade-jailfree-receive-${cIdx}`} className="trade-prop-checkbox-item">
                        <input 
                          type="checkbox"
                          checked={tradeReceiveJailCardsCount > cIdx}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTradeReceiveJailCardsCount(cIdx + 1);
                            } else {
                              setTradeReceiveJailCardsCount(cIdx);
                            }
                          }}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="prop-bullet" style={{ background: '#eab308' }}></span>
                        <span className="prop-name-lbl">🔑 Tahliye Kartı ({card.isChance ? 'Şans' : 'Kamu'})</span>
                      </label>
                    ))}
                    {propertiesData.filter(pd => gameData.properties[pd.id]?.ownerPlayerId === tradeReceiverId).length === 0 && (!receiverPlayer?.jailFreeCards || receiverPlayer.jailFreeCards.length === 0) && (
                      <div className="no-props-txt">Oyuncunun takas edilebilir mülkü yok.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="trade-builder-footer">
            <button 
              className="trade-submit-btn"
              disabled={!tradeReceiverId || (tradeGiveMoney === 0 && tradeGiveProps.length === 0 && tradeGiveJailCardsCount === 0 && tradeReceiveMoney === 0 && tradeReceiveProps.length === 0 && tradeReceiveJailCardsCount === 0)}
              onClick={() => {
                if (counteringTradeId) {
                  sendCommand('REJECT_TRADE', { tradeId: counteringTradeId });
                  setCounteringTradeId(null);
                }
                sendCommand('PROPOSE_TRADE', {
                  receiverId: tradeReceiverId,
                  giveMoney: tradeGiveMoney,
                  giveProperties: tradeGiveProps,
                  giveJailCards: tradeGiveJailCardsCount,
                  receiveMoney: tradeReceiveMoney,
                  receiveProperties: tradeReceiveProps,
                  receiveJailCards: tradeReceiveJailCardsCount
                });
                setIsProposingTrade(false);
                sendCommand('CANCEL_TRADE_PROPOSAL');
                setTradeReceiverId('');
                setTradeGiveMoney(0);
                setTradeReceiveMoney(0);
                setTradeGiveProps([]);
                setTradeReceiveProps([]);
                setTradeGiveJailCardsCount(0);
                setTradeReceiveJailCardsCount(0);
              }}
            >
              {counteringTradeId ? `${t('trades.counter', 'Karşı Teklif')} 🔄` : `${t('trades.make_proposal', 'Teklifi Gönder')} 🤝`}
            </button>
            <button className="trade-cancel-btn" onClick={() => {
              setIsProposingTrade(false);
              sendCommand('CANCEL_TRADE_PROPOSAL');
              setTradeReceiverId('');
              setTradeGiveMoney(0);
              setTradeReceiveMoney(0);
              setTradeGiveProps([]);
              setTradeReceiveProps([]);
              setTradeGiveJailCardsCount(0);
              setTradeReceiveJailCardsCount(0);
            }}>Vazgeç ❌</button>
          </div>
        </div>
      </div>
      </div>,
      document.body
    );
  };

  // 2. Click-to-Inspect Detailed Tile Inspector Modal
  const renderTileInspector = (id) => {
    if (id === 'jail_card') {
      return createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
          <div 
            className="deed-backdrop-overlay" 
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={() => setSelectedPropertyId(null)}
          />
          <div className="stage-property-card-wrapper selected-deed-info animate-card-fly" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10000 }}>
            <button className="deed-close-btn" onClick={() => setSelectedPropertyId(null)}>✕</button>
            <div className="deed-card special-card" style={{ boxShadow: '0 12px 40px rgba(234, 179, 8, 0.3)' }}>
              <div className="deed-color-header" style={{ background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)', color: '#0f172a' }}>
                KORDON AĞALARI
              </div>
              <div className="deed-body" style={{ textAlign: 'center', padding: '20px 16px' }}>
                <h4 className="deed-name" style={{ color: '#eab308', fontSize: '1.05rem', margin: '0 0 10px 0' }}>🔑 KARAKOL'DAN ÜCRETSİZ ÇIKIŞ KARTI</h4>
                <div style={{
                  fontSize: '2.5rem',
                  margin: '12px 0',
                  filter: 'drop-shadow(0 4px 8px rgba(234, 179, 8, 0.4))'
                }}>🔑</div>
                <p style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.4', margin: '0 0 15px 0' }}>
                  Bu kart size Buca Karakolu'na (Çiğli Sürgünü) girdiğinizde <strong>ücretsiz olarak tahliye olma</strong> hakkı verir.
                </p>
                <div style={{ textAlign: 'left', background: 'rgba(255,255,255,0.03)', border: '1.2px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '10px 12px', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  <strong>Kullanım Rehberi:</strong>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '14px' }}>
                    <li style={{ marginBottom: '4px' }}>Buca Karakolu'na (Çiğli Sürgünü) gönderildiğinizde kendi sıranızda zarlarla şans aramadan veya ₺50 kefaret ödemeden anında serbest kalabilirsiniz.</li>
                    <li>Kart kullanıldıktan sonra desteye geri iade edilir.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      );
    }
    const tile = getPropertyDetail(id);
    if (!tile) return null;
    const propState = gameData.properties[tile.id];
    const isOwner = propState?.ownerPlayerId === myPlayerId;
    const isActionAllowed = gameData && !['LOBBY', 'GAME_SETUP', 'ORDER_ROLL', 'GAME_OVER'].includes(gameData.phase);
    const ownerPlayer = propState?.ownerPlayerId 
      ? gameData.players.find(p => p.playerId === propState.ownerPlayerId)
      : null;
    const isOwned = ownerPlayer !== null;
    const mortgageVal = tile.mortgageValue || Math.floor((tile.price || 0) / 2);
    const unmortgageVal = Math.ceil(mortgageVal * 1.1);
    
    const ownerProps = ownerPlayer ? ownerPlayer.properties || [] : [];
    const ownsGroup = isOwned && playerOwnsFullGroup(propState?.ownerPlayerId, tile.color);
    const railroadsOwned = ownerProps.filter(id => getPropertyDetail(id)?.type === 'railroad').length;
    const utilitiesOwned = ownerProps.filter(id => getPropertyDetail(id)?.type === 'utility').length;

    const isBaseActive = !propState?.mortgaged && isOwned && propState?.houses === 0 && !propState?.hotel && !ownsGroup;
    const isGroupActive = !propState?.mortgaged && isOwned && propState?.houses === 0 && !propState?.hotel && ownsGroup;
    const isHouse1Active = !propState?.mortgaged && isOwned && propState?.houses === 1;
    const isHouse2Active = !propState?.mortgaged && isOwned && propState?.houses === 2;
    const isHouse3Active = !propState?.mortgaged && isOwned && propState?.houses === 3;
    const isHouse4Active = !propState?.mortgaged && isOwned && propState?.houses === 4;
    const isHotelActive = !propState?.mortgaged && isOwned && propState?.hotel;

    const isRail1Active = !propState?.mortgaged && isOwned && railroadsOwned === 1;
    const isRail2Active = !propState?.mortgaged && isOwned && railroadsOwned === 2;
    const isRail3Active = !propState?.mortgaged && isOwned && railroadsOwned === 3;
    const isRail4Active = !propState?.mortgaged && isOwned && railroadsOwned === 4;

    const isUtil1Active = !propState?.mortgaged && isOwned && utilitiesOwned === 1;
    const isUtil2Active = !propState?.mortgaged && isOwned && utilitiesOwned === 2;
    
    return createPortal(
      <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
        {/* Backdrop for click outside to close */}
        <div 
          className="deed-backdrop-overlay" 
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelectedPropertyId(null)}
        />
        <div className="stage-property-card-wrapper selected-deed-info animate-card-fly" style={{ position: 'relative', zIndex: 10000, width: '90%', maxWidth: '320px', maxHeight: '92vh', overflowY: 'auto' }}>
          <button className="deed-close-btn" onClick={() => setSelectedPropertyId(null)}>✕</button>
          
          {/* Tile Navigation Arrows */}
          {typeof id === 'number' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
              <button 
                onClick={(e) => { e.stopPropagation(); if (navigateDeed) navigateDeed(id, -1); }}
                style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                ◀ Önceki
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); if (navigateDeed) navigateDeed(id, 1); }}
                style={{ background: 'rgba(15, 23, 42, 0.85)', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                Sonraki ▶
              </button>
            </div>
          )}
        
        {tile.type === 'property' && (
          <div className={`deed-card ${propState?.mortgaged ? 'mortgaged' : ''}`}>
            <div className="deed-color-header" style={{ background: tile.color || '#4b5563' }}>
              TAPU SENEDİ
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              
              {propState?.mortgaged && (
                <div className="deed-mortgaged-badge" style={{ background: '#f43f5e', color: '#fff', fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px', borderRadius: '4px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🔒 İPOTEKLİ
                </div>
              )}
              {!propState?.mortgaged && propState?.hotel && (
                <div className="deed-construction-status" style={{ background: '#10b981', color: '#fff', fontSize: '0.72rem', fontWeight: 900, padding: '4px 8px', borderRadius: '4px', marginTop: '6px', textAlign: 'center' }}>
                  🏛️ AĞA YALISI {propState.houses > 0 ? `+ ${propState.houses} EKSTRA TEZGÂH (${5 + propState.houses}. KAT)` : 'İNŞA EDİLMİŞ'}
                </div>
              )}
              {!propState?.mortgaged && !propState?.hotel && propState?.houses > 0 && (
                <div className="deed-construction-status" style={{ background: '#10b981', color: '#fff', fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px', borderRadius: '4px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                  {Array.from({ length: Math.min(propState.houses, 4) }).map((_, i) => <span key={i}>🛖</span>)} {propState.houses}. TEZGÂH İNŞA EDİLMİŞ
                </div>
              )}

              <div className="deed-divider"></div>
              
              <div className="deed-owner-status">
                {isOwned ? (
                  <div className="owner-badge-inline">
                    <span>Sahibi: </span>
                    <TokenIcon token={ownerPlayer.token} color={ownerPlayer.color} size={22} />
                    <strong style={{ color: ownerPlayer.color, marginLeft: '4px' }}>{(ownerPlayer.name && ownerPlayer.name !== 'null') ? ownerPlayer.name : 'Ağa'}</strong>
                    {isOwner && ' (Sen)'}
                  </div>
                ) : (
                  <div className="owner-badge-inline unowned">
                    <span>Sahibi: </span>
                    <strong>Sahipsiz</strong>
                  </div>
                )}
              </div>
              <div className="deed-divider"></div>

              <div className={`deed-rent-row ${isBaseActive ? 'active-rent-tier' : ''}`}><span>Kira (Boş)</span> <strong>₺{tile.rent}</strong></div>
              <div className={`deed-rent-row ${isGroupActive ? 'active-rent-tier' : ''}`}><span>Tam Grup (2x)</span> <strong>₺{tile.rent * 2}</strong></div>
              {tile.houseRents && (
                <>
                  <div className={`deed-rent-row small ${isHouse1Active ? 'active-rent-tier' : ''}`}><span>1. Tezgâh</span> <strong>₺{tile.houseRents[0]}</strong></div>
                  <div className={`deed-rent-row small ${isHouse2Active ? 'active-rent-tier' : ''}`}><span>2. Tezgâh</span> <strong>₺{tile.houseRents[1]}</strong></div>
                  <div className={`deed-rent-row small ${isHouse3Active ? 'active-rent-tier' : ''}`}><span>3. Tezgâh</span> <strong>₺{tile.houseRents[2]}</strong></div>
                  <div className={`deed-rent-row small ${isHouse4Active ? 'active-rent-tier' : ''}`}><span>4. Tezgâh</span> <strong>₺{tile.houseRents[3]}</strong></div>
                  <div className={`deed-rent-row small ${isHotelActive && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>🏛️ Ağa Yalısı</span> <strong>₺{tile.hotelRent}</strong></div>
                  {propState?.hotel && propState?.houses > 0 && (
                    <div className="deed-rent-row small active-rent-tier" style={{ background: 'rgba(34, 197, 94, 0.25)', borderColor: '#22c55e', margin: '4px 0' }}>
                      <span>🔥 {5 + propState.houses}. Kat Kirası (Otel + {propState.houses} Tezgâh)</span>
                      <strong style={{ color: '#4ade80' }}>₺{(tile.hotelRent + propState.houses * (tile.houseRents[3] || tile.hotelRent)) * (gameData?.settings?.rentMultiplier || 1)}</strong>
                    </div>
                  )}
                  <div className="deed-divider"></div>
                  <div className="deed-rent-row small"><span>Tezgâh Maliyeti</span> <strong>₺{tile.houseCost}</strong></div>
                  <div className="deed-rent-row small"><span>Ağa Yalısı Maliyeti</span> <strong>₺{tile.houseCost} (4 Tezgâh Sonrası)</strong></div>
                </>
              )}
              <div className="deed-divider"></div>
              <div className="deed-mortgage-info-row" style={{ width: '100%', fontSize: '0.74rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpotek Değeri:</span> <strong>₺{mortgageVal}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpoteği Kaldırma Bedeli:</span> <strong>₺{unmortgageVal}</strong>
                </div>
              </div>
              <div className="deed-price-row">Mülk Fiyatı: <strong>₺{tile.price}</strong></div>
            </div>
          </div>
        )}

        {tile.type === 'railroad' && (
          <div className={`deed-card transport-card ${propState?.mortgaged ? 'mortgaged' : ''}`}>
            <div className="deed-color-header" style={{ background: '#1e293b' }}>
              ULAŞIM KARTI
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              
              {propState?.mortgaged && (
                <div className="deed-mortgaged-badge" style={{ background: '#f43f5e', color: '#fff', fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px', borderRadius: '4px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🔒 İPOTEKLİ
                </div>
              )}
              
              <div className="deed-divider"></div>
              
              <div className="deed-owner-status">
                {isOwned ? (
                  <div className="owner-badge-inline">
                    <span>Sahibi: </span>
                    <TokenIcon token={ownerPlayer.token} color={ownerPlayer.color} size={22} />
                    <strong style={{ color: ownerPlayer.color, marginLeft: '4px' }}>{(ownerPlayer.name && ownerPlayer.name !== 'null') ? ownerPlayer.name : 'Ağa'}</strong>
                    {isOwner && ' (Sen)'}
                  </div>
                ) : (
                  <div className="owner-badge-inline unowned">
                    <span>Sahibi: </span>
                    <strong>Sahipsiz</strong>
                  </div>
                )}
              </div>
              <div className="deed-divider"></div>

              {!propState?.mortgaged && propState?.houses > 0 && (
                <div style={{ background: 'rgba(34, 197, 94, 0.18)', border: '1.5px solid #22c55e', color: '#4ade80', borderRadius: '8px', padding: '6px 10px', margin: '8px 0', textAlign: 'center', fontSize: '0.82rem', fontWeight: 800 }}>
                  🛖 {propState.houses}. TEZGÂH İNŞA EDİLMİŞ
                  <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginTop: '2px' }}>
                    ⚡ Kira Çarpanı: 2<sup>{propState.houses}</sup> = {Math.pow(2, propState.houses)}x Katlandı!
                  </div>
                </div>
              )}

              <div className={`deed-rent-row ${isRail1Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>1 İstasyon Sahibi</span> <strong>₺25</strong></div>
              <div className={`deed-rent-row ${isRail2Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>2 İstasyon Sahibi</span> <strong>₺50</strong></div>
              <div className={`deed-rent-row ${isRail3Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>3 İstasyon Sahibi</span> <strong>₺100</strong></div>
              <div className={`deed-rent-row ${isRail4Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>4 İstasyon Sahibi</span> <strong>₺200</strong></div>
              {propState?.houses > 0 && (
                <div className="deed-rent-row active-rent-tier" style={{ background: 'rgba(34, 197, 94, 0.25)', borderColor: '#22c55e', margin: '6px 0' }}>
                  <span>🔥 Tezgâhlı Katlanmış Kira ({Math.pow(2, propState.houses)}x)</span>
                  <strong style={{ color: '#4ade80', fontSize: '1rem' }}>
                    ₺{Math.pow(2, (getBoardData(gameData).filter(p => p.type === 'railroad' && gameData.properties[p.id]?.ownerPlayerId === propState.ownerPlayerId).length || 1) - 1) * 25 * Math.pow(2, propState.houses) * (gameData.settings?.rentMultiplier || 1)}
                  </strong>
                </div>
              )}
              <div className="deed-divider"></div>
              <div className="deed-mortgage-info-row" style={{ width: '100%', fontSize: '0.74rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpotek Değeri:</span> <strong>₺{mortgageVal}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpoteği Kaldırma Bedeli:</span> <strong>₺{unmortgageVal}</strong>
                </div>
              </div>
              <div className="deed-price-row">Mülk Fiyatı: <strong>₺{tile.price}</strong></div>
            </div>
          </div>
        )}

        {tile.type === 'utility' && (
          <div className={`deed-card utility-card ${propState?.mortgaged ? 'mortgaged' : ''}`}>
            <div className="deed-color-header" style={{ background: '#374151' }}>
              ALTYAPI KARTI
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              
              {propState?.mortgaged && (
                <div className="deed-mortgaged-badge" style={{ background: '#f43f5e', color: '#fff', fontSize: '0.72rem', fontWeight: 900, padding: '3px 8px', borderRadius: '4px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🔒 İPOTEKLİ
                </div>
              )}
              
              <div className="deed-divider"></div>
              
              <div className="deed-owner-status">
                {isOwned ? (
                  <div className="owner-badge-inline">
                    <span>Sahibi: </span>
                    <TokenIcon token={ownerPlayer.token} color={ownerPlayer.color} size={22} />
                    <strong style={{ color: ownerPlayer.color, marginLeft: '4px' }}>{(ownerPlayer.name && ownerPlayer.name !== 'null') ? ownerPlayer.name : 'Ağa'}</strong>
                    {isOwner && ' (Sen)'}
                  </div>
                ) : (
                  <div className="owner-badge-inline unowned">
                    <span>Sahibi: </span>
                    <strong>Sahipsiz</strong>
                  </div>
                )}
              </div>
              <div className="deed-divider"></div>

              {!propState?.mortgaged && propState?.houses > 0 && (
                <div style={{ background: 'rgba(34, 197, 94, 0.18)', border: '1.5px solid #22c55e', color: '#4ade80', borderRadius: '8px', padding: '6px 10px', margin: '8px 0', textAlign: 'center', fontSize: '0.82rem', fontWeight: 800 }}>
                  🛖 {propState.houses}. TEZGÂH İNŞA EDİLMİŞ
                  <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginTop: '2px' }}>
                    ⚡ Kira Çarpanı: 2<sup>{propState.houses}</sup> = {Math.pow(2, propState.houses)}x Katlandı!
                  </div>
                </div>
              )}

              <div className={`deed-rent-row ${isUtil1Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>1 Altyapı Sahibi</span> <strong>Zarın 4 Katı</strong></div>
              <div className={`deed-rent-row ${isUtil2Active && (!propState?.houses || propState.houses === 0) ? 'active-rent-tier' : ''}`}><span>2 Altyapı Sahibi</span> <strong>Zarın 10 Katı</strong></div>
              {propState?.houses > 0 && (
                <div className="deed-rent-row active-rent-tier" style={{ background: 'rgba(34, 197, 94, 0.25)', borderColor: '#22c55e', margin: '6px 0' }}>
                  <span>🔥 Tezgâhlı Katlanmış Kira ({Math.pow(2, propState.houses)}x)</span>
                  <strong style={{ color: '#4ade80', fontSize: '0.92rem' }}>
                    Zarın {(getBoardData(gameData).filter(p => p.type === 'utility' && gameData.properties[p.id]?.ownerPlayerId === propState.ownerPlayerId).length === 2 ? 10 : 4) * Math.pow(2, propState.houses)} Katı!
                  </strong>
                </div>
              )}
              <div className="deed-divider"></div>
              <div className="deed-mortgage-info-row" style={{ width: '100%', fontSize: '0.74rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpotek Değeri:</span> <strong>₺{mortgageVal}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>İpoteği Kaldırma Bedeli:</span> <strong>₺{unmortgageVal}</strong>
                </div>
              </div>
              <div className="deed-price-row">Mülk Fiyatı: <strong>₺{tile.price}</strong></div>
            </div>
          </div>
        )}

        {tile.type === 'chance' && (
          <div className="deed-card info-card chance-theme">
            <div className="deed-color-header" style={{ background: '#ea580c' }}>
              ŞANS KARESİ
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Bu kareye geldiğinizde kaderinizi belirleyecek rastgele bir **Şans Kartı** çekersiniz.
              </p>
              <div className="deed-divider"></div>
              <p className="card-info-subtext">Kartlar olumlu ya da olumsuz etkiler barındırabilir.</p>
            </div>
          </div>
        )}

        {tile.type === 'community_chest' && (
          <div className="deed-card info-card community-theme">
            <div className="deed-color-header" style={{ background: '#0284c7' }}>
              KAMU FONU
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Bu kareye geldiğinizde sandıktan tüm oyuncuları ilgilendiren bir **Kamu Fonu Kartı** çekersiniz.
              </p>
              <div className="deed-divider"></div>
              <p className="card-info-subtext">Genellikle toplumsal ödemeler veya fon yardımları içerir.</p>
            </div>
          </div>
        )}

        {tile.type === 'parking' && (
          <div className="deed-card info-card parking-theme">
            <div className="deed-color-header" style={{ background: '#16a34a' }}>
              ÜCRETSİZ OTOPARK
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Herkesin mola verdiği, vergi ve cezaların toplandığı güvenli bölge. Buradayken ceza ödemezsiniz.
              </p>
              <div className="deed-divider"></div>
              <div className="deed-rent-row"><span>Mevcut Havuz</span> <strong>₺{gameData.freeParkingPool || 0}</strong></div>
              <p className="card-info-subtext">Kural: Otoparka tam olarak basan oyuncu havuzda biriken tüm parayı alır.</p>
            </div>
          </div>
        )}

        {tile.type === 'jail' && (
          <div className="deed-card info-card jail-theme">
            <div className="deed-color-header" style={{ background: '#4b5563' }}>
              ÇİĞLİ SÜRGÜNÜ / ZİYARETÇİ
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Çiğli Sürgünü karesindesiniz. Sadece ziyaretçi olarak basanlar güvendedir. Tutuklu olan oyuncular sürgün kurallarına tabidir.
              </p>
              <div className="deed-divider"></div>
              <div className="jail-inmates-list">
                <span>İçeridekiler:</span>
                {gameData.players.filter(p => p.inJail).map(p => (
                  <div key={p.playerId} className="inmate-badge" style={{ color: p.color }}>
                    <TokenIcon token={p.token} color={p.color} size={20} />
                    <span style={{ marginLeft: '4px' }}>{p.name}</span>
                  </div>
                ))}
                {gameData.players.filter(p => p.inJail).length === 0 && (
                  <div className="no-inmates">Şu an hapiste kimse yok.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tile.type === 'go_to_jail' && (
          <div className="deed-card info-card go-to-jail-theme">
            <div className="deed-color-header" style={{ background: '#dc2626' }}>
              KARAKOLA GİT
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Bu kareye bastığınızda doğrudan Buca Karakolu'na (Çiğli Sürgünü) gönderilirsiniz.
              </p>
              <div className="deed-divider"></div>
              <p className="card-info-subtext">Önemli: Başlangıç noktasından geçemezsiniz ve ₺200 geçiş ücreti alamazsınız.</p>
            </div>
          </div>
        )}

        {tile.type === 'tax' && (
          <div className="deed-card info-card tax-theme">
            <div className="deed-color-header" style={{ background: '#7c2d12' }}>
              VERGİ CEZASI
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                Belediyenin yerel düzenlemeleri kapsamında ödemeniz gereken ceza / vergi.
              </p>
              <div className="deed-divider"></div>
              <div className="deed-rent-row"><span>Ödeme Miktarı</span> <strong>₺{tile.price || 200}</strong></div>
              <p className="card-info-subtext">House Rule: Ödenen cezalar Ücretsiz Otopark havuzuna aktarılır.</p>
            </div>
          </div>
        )}

        {tile.type === 'go' && (
          <div className="deed-card info-card go-theme">
            <div className="deed-color-header" style={{ background: '#1e3a8a' }}>
              BAŞLANGIÇ NOKTASI
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              <p className="card-info-text">
                {gameBrand.name} başlangıç karesi. Her tur tamamlandığında buradan geçersiniz.
              </p>
              <div className="deed-divider"></div>
              <div className="deed-rent-row"><span>Geçiş Maaşı</span> <strong>₺200</strong></div>
              <p className="card-info-subtext">Tam üzerine basan oyuncu iki kat maaş (₺400) alır.</p>
            </div>
          </div>
        )}

        {/* Building / Management Buttons (If Owned by Local Player) */}
        {isOwner && (tile.type === 'property' || (tile.type !== 'property' && gameData.settings?.buildOnSpecialTiles === true)) && !gameData.winnerPlayerId && (
          <div className="deed-build-actions animate-pop-in">
            {!propState.mortgaged ? (
              <>
                <div className="build-action-row">
                  {(() => {
                    const allowWithoutMonopoly = gameData.settings?.buildWithoutMonopoly === true || tile.type !== 'property';
                    const hasMonopolyOrCrazy = allowWithoutMonopoly || playerOwnsFullGroup(myPlayerId, tile.color);
                    const houseCost = tile.houseCost || tile.price || 100;
                    const canBuildMore = gameData.settings?.unlimitedBuildingLevel === true || !propState.hotel;
                    const isBuildDisabled = !isActionAllowed || !hasMonopolyOrCrazy || !canBuildMore || (me?.money < houseCost);

                    return (
                      <button 
                        className="build-action-btn green" 
                        onClick={() => sendCommand('BUY_HOUSE', { tileId: tile.id })}
                        disabled={isBuildDisabled}
                        title={!isActionAllowed ? "Bu aşamada bina inşa edemezsiniz." : (!hasMonopolyOrCrazy ? "Gruptaki tüm tapulara sahip olmalısınız." : (!canBuildMore ? "Maksimum bina seviyesine ulaşıldı." : (me?.money < houseCost ? "Yetersiz bakiye" : "")))}
                      >
                        {propState.houses === 4 && !gameData.settings?.unlimitedBuildingLevel ? `Ağa Yalısı Kur (+₺${houseCost})` : `Tezgâh Kur (+₺${houseCost})`}
                      </button>
                    );
                  })()}
                  <button 
                    className="build-action-btn orange" 
                    onClick={() => sendCommand('SELL_HOUSE', { tileId: tile.id })}
                    disabled={!isActionAllowed || (propState.houses === 0 && !propState.hotel)}
                    title={!isActionAllowed ? "Bu aşamada bina satamazsınız." : ""}
                  >
                    {propState.hotel ? `Ağa Yalısı Sat (-₺${Math.floor(tile.houseCost / 2)})` : `Tezgâh Sat (-₺${Math.floor(tile.houseCost / 2)})`}
                  </button>
                </div>
                <button 
                  className="build-action-btn green" 
                  onClick={() => sendCommand('MORTGAGE_PROPERTY', { tileId: tile.id })}
                  disabled={!isActionAllowed}
                  title={!isActionAllowed ? "Bu aşamada ipotek edemezsiniz." : ""}
                >
                  İpotek Et (+₺{mortgageVal})
                </button>
                {gameData.settings?.sellPropertyToBankAt75Percent !== false && !propState.mortgaged && (
                  <button 
                    className="build-action-btn red" 
                    onClick={() => sendCommand('SELL_PROPERTY_TO_BANK', { tileId: tile.id })}
                    disabled={!isActionAllowed}
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', marginTop: '6px' }}
                    title={!isActionAllowed ? "Bu aşamada tapu satamazsınız." : "Tapuyu %75 fiyatına bankaya geri sat"}
                  >
                    🏛️ Bankaya Sat (%75: +₺{Math.floor((tile.price || 100) * 0.75)})
                  </button>
                )}
              </>
            ) : (
              <button 
                className="build-action-btn red" 
                onClick={() => sendCommand('UNMORTGAGE_PROPERTY', { tileId: tile.id })}
                disabled={!isActionAllowed || me?.money < unmortgageVal}
                title={!isActionAllowed ? "Bu aşamada ipoteği kaldıramazsınız." : ""}
              >
                İpoteği Kaldır (-₺{unmortgageVal})
              </button>
            )}
          </div>
        )}

        {/* Railroad / Utility mortgage and build actions */}
        {isOwner && (tile.type === 'railroad' || tile.type === 'utility') && !gameData.winnerPlayerId && (
          <div className="deed-build-actions animate-pop-in" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {gameData.settings?.buildOnSpecialTiles && !propState.mortgaged && (
              <button 
                className="build-action-btn green" 
                onClick={() => sendCommand('BUY_HOUSE', { tileId: tile.id })}
                disabled={!isActionAllowed || me?.money < (tile.price || 150)}
                title={me?.money < (tile.price || 150) ? "Yetersiz bakiye" : "İstasyona/Kuruma ev kur (Kira 2x katlanır)"}
              >
                🔨 Tezgâh Kur (-₺{tile.price || 150})
              </button>
            )}
            {!propState.mortgaged ? (
              <button 
                className="build-action-btn green" 
                onClick={() => sendCommand('MORTGAGE_PROPERTY', { tileId: tile.id })}
                disabled={!isActionAllowed}
                title={!isActionAllowed ? "Bu aşamada ipotek edemezsiniz." : ""}
              >
                İpotek Et (+₺{mortgageVal})
              </button>
            ) : (
              <button 
                className="build-action-btn red" 
                onClick={() => sendCommand('UNMORTGAGE_PROPERTY', { tileId: tile.id })}
                disabled={!isActionAllowed || me?.money < unmortgageVal}
                title={!isActionAllowed ? "Bu aşamada ipoteği kaldıramazsınız." : ""}
              >
                İpoteği Kaldır (-₺{unmortgageVal})
              </button>
            )}
          </div>
        )}
        {isOwned && !isOwner && !gameData.winnerPlayerId && (
          <div className="deed-build-actions animate-pop-in" style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
            <button 
              className="gold-action-btn pulse-glow"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 0 15px rgba(16, 185, 129, 0.4)',
                border: 'none',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
              onClick={() => {
                setTradeReceiverId(ownerPlayer.playerId);
                setTradeReceiveProps([tile.id]);
                setTradeGiveProps([]);
                setTradeGiveMoney(0);
                setTradeReceiveMoney(0);
                setTradeGiveJailCardsCount(0);
                setTradeReceiveJailCardsCount(0);
                
                setIsProposingTrade(true);
                sendCommand('START_TRADE_PROPOSAL');
                setSelectedPropertyId(null);
              }}
              disabled={['LOBBY', 'GAME_SETUP', 'ORDER_ROLL', 'GAME_OVER'].includes(gameData?.phase)}
              title={['LOBBY', 'GAME_SETUP', 'ORDER_ROLL', 'GAME_OVER'].includes(gameData?.phase) ? "Bu aşamada takas teklif edemezsiniz." : "Bu oyuncu ile takas başlat."}
            >
              🤝 Teklif Ver (Takas)
            </button>
          </div>
        )}
        </div>
      </div>,
      document.body
    );
  };

  // ====================================================
  // RIGHT COLUMN ACTION RENDERERS
  // ====================================================

  const renderCardGraphic = (isChance, id) => {
    let src = '/assets/chance_card_header.jpg';
    if (isChance) {
      switch (id) {
        case 1:
        case 4:
        case 7:
        case 14:
          src = '/assets/izmir_money_win.jpg';
          break;
        case 2:
        case 6:
        case 13:
          src = '/assets/izmir_money_lose.jpg';
          break;
        case 3:
        case 8:
        case 9:
        case 11:
          src = '/assets/izmir_metro.jpg';
          break;
        case 5:
          src = '/assets/izmir_festival.jpg';
          break;
        case 10:
        case 12:
          src = '/assets/izmir_jail.jpg';
          break;
        default:
          src = '/assets/izmir_beach.jpg';
          break;
      }
    } else {
      switch (id) {
        case 1:
        case 2:
        case 4:
        case 8:
        case 12:
        case 13:
          src = '/assets/izmir_money_win.jpg';
          break;
        case 3:
        case 9:
        case 11:
        case 14:
          src = '/assets/izmir_money_lose.jpg';
          break;
        case 5:
        case 7:
          src = '/assets/izmir_festival.jpg';
          break;
        case 6:
        case 10:
          src = '/assets/izmir_jail.jpg';
          break;
        default:
          src = '/assets/izmir_beach.jpg';
          break;
      }
    }
    return (
      <img 
        src={src} 
        alt="Kart Görseli" 
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover', 
          borderRadius: '12px', 
          border: '2px solid rgba(255,255,255,0.2)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.4)'
        }} 
      />
    );
  };

  // 1. Drawn Card Overlay (Chance / Community Chest)
  const renderCardReveal = () => {
    if (!isMyTurn && !showOthersCards) return null;
    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 9999 }}>
        <div className="modal-backdrop" onClick={() => sendCommand('RESOLVE_CARD')}></div>
        <div className={`drawn-card-deed animate-pop-in ${cardToRender.isChance ? 'chance-card-theme' : 'community-card-theme'}`} style={{ position: 'relative', zIndex: 10000 }}>
          <button className="deed-close-btn" onClick={() => sendCommand('RESOLVE_CARD')}>✕</button>
          <div className="card-deed-icon" style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            {renderCardGraphic(cardToRender.isChance, cardToRender.id)}
          </div>
          <h4 className="card-deed-header">
            {cardToRender.isChance ? 'ŞANS KARTI' : 'KAMU FONU'}
          </h4>
          <p className="card-deed-text">{cardToRender.text}</p>
          
          {isMyTurn ? (
            <button className="gold-action-btn small" onClick={() => sendCommand('RESOLVE_CARD')} style={{ marginTop: '16px' }}>
              Kartı Tamamla 👍
            </button>
          ) : (
            <div className="card-waiting-text" style={{ marginTop: '16px' }}>{activePlayer?.name} kartı okuyor...</div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 2. Property Buying / Auctioning Decision card
  const renderPropertyDecision = () => {
    if (!isMyTurn && !showOthersCards) return null;
    const tile = getPropertyDetail(gameData.pendingAction.tileId);
    if (!tile) return null;

    const myProperties = me?.properties || [];
    const raiseMoneyTargets = myProperties.map(id => {
      const t = getPropertyDetail(id);
      const propState = gameData.properties[id];
      if (!t || !propState) return null;

      const hasHouses = propState.houses > 0 || propState.hotel;
      const canMortgage = !propState.mortgaged && !hasHouses;

      if (!hasHouses && !canMortgage) return null;

      return {
        tile: t,
        propState,
        hasHouses,
        canMortgage
      };
    }).filter(Boolean);

    return createPortal(
      <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
        <div className="modal-backdrop"></div>
        <div className="stage-property-card-wrapper animate-card-fly" style={{ position: 'relative', zIndex: 10002, width: '90%', maxWidth: '320px', maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div className="deed-card animate-pop-in" style={{ margin: 0 }}>
            <div className="deed-color-header" style={{ background: tile.color || '#4b5563' }}>
              TAPU SENEDİ
            </div>
            <div className="deed-body">
              <h4 className="deed-name">{tile.name}</h4>
              <div className="deed-divider"></div>
              {tile.type === 'property' && (
                <>
                  <div className="deed-rent-row"><span>Kira</span> <strong>₺{tile.rent}</strong></div>
                  <div className="deed-rent-row"><span>Grup Kira (2x)</span> <strong>₺{tile.rent * 2}</strong></div>
                  {tile.houseRents && (
                    <>
                      <div className="deed-rent-row small"><span>1. Tezgâh</span> <strong>₺{tile.houseRents[0]}</strong></div>
                      <div className="deed-rent-row small"><span>2. Tezgâh</span> <strong>₺{tile.houseRents[1]}</strong></div>
                      <div className="deed-rent-row small"><span>3. Tezgâh</span> <strong>₺{tile.houseRents[2]}</strong></div>
                      <div className="deed-rent-row small"><span>4. Tezgâh</span> <strong>₺{tile.houseRents[3]}</strong></div>
                      <div className="deed-rent-row small"><span>🏛️ Ağa Yalısı</span> <strong>₺{tile.hotelRent}</strong></div>
                      <div className="deed-divider"></div>
                      <div className="deed-rent-row small"><span>Tezgâh Maliyeti</span> <strong>₺{tile.houseCost}</strong></div>
                      <div className="deed-rent-row small"><span>Ağa Yalısı Maliyeti</span> <strong>₺{tile.houseCost} (4 Tezgâh Sonrası)</strong></div>
                      <div className="deed-divider"></div>
                      <div className="deed-rent-row small" style={{ color: '#94a3b8' }}><span>İpotek Değeri</span> <strong>₺{Math.floor(tile.price / 2)}</strong></div>
                    </>
                  )}
                </>
              )}
              {tile.type === 'railroad' && (
                <>
                  <div className="deed-rent-row"><span>1 İstasyon Sahibi</span> <strong>₺250</strong></div>
                  <div className="deed-rent-row"><span>2 İstasyon Sahibi</span> <strong>₺500</strong></div>
                  <div className="deed-rent-row"><span>3 İstasyon Sahibi</span> <strong>₺750</strong></div>
                  <div className="deed-rent-row"><span>4 İstasyon Sahibi</span> <strong>₺1000</strong></div>
                </>
              )}
              {tile.type === 'utility' && (
                <>
                  <div className="deed-rent-row"><span>1 Altyapı Sahibi</span> <strong>Zarın 4 Katı</strong></div>
                  <div className="deed-rent-row"><span>2 Altyapı Sahibi</span> <strong>Zarın 10 Katı</strong></div>
                </>
              )}
              <div className="deed-price-row">Fiyat: <strong>₺{tile.price}</strong></div>
            </div>
          </div>

          {/* Quick Property Management for Raising Money */}
          {isMyTurn && raiseMoneyTargets.length > 0 && me?.money < tile.price && (
            <div className="raise-money-modal-panel" style={{
              width: '100%',
              maxWidth: '340px',
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1.5px solid rgba(251, 191, 36, 0.35)',
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              color: '#fff',
              boxSizing: 'border-box'
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fbbf24', textAlign: 'center', marginBottom: '8px' }}>
                💸 PARA YARATMAK İÇİN MÜLKLERİNİZİ YÖNETİN
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
                {raiseMoneyTargets.map(({ tile: t, hasHouses, canMortgage }) => {
                  const houseValue = Math.floor(t.houseCost / 2);
                  const mortgageValue = Math.floor(t.price / 2);
                  return (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '6px 8px', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: t.color || '#fff', display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{t.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {hasHouses && (
                          <button 
                            className="quick-btn" 
                            style={{ fontSize: '0.7rem', padding: '3px 6px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => sendCommand('SELL_HOUSE', { tileId: t.id })}
                          >
                            Tezgâh Sat (+₺{houseValue})
                          </button>
                        )}
                        {canMortgage && (
                          <button 
                            className="quick-btn" 
                            style={{ fontSize: '0.7rem', padding: '3px 6px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => sendCommand('MORTGAGE_PROPERTY', { tileId: t.id })}
                          >
                            İpotek (+₺{mortgageValue})
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isMyTurn ? (
            <div className="deed-actions" style={{ width: '100%', maxWidth: '340px', display: 'flex', gap: '8px' }}>
              <button 
                className="deed-btn buy pulse-glow" 
                onClick={() => sendCommand('BUY_PROPERTY')} 
                disabled={me?.money < tile.price}
                style={{ flex: 1, padding: '12px 0' }}
              >
                Satın Al (₺{tile.price})
              </button>
              <button 
                className="deed-btn auction" 
                onClick={() => sendCommand('AUCTION_PROPERTY')}
                style={{ flex: 1, padding: '12px 0' }}
              >
                Açık Artırma
              </button>
            </div>
          ) : (
            <div className="deed-waiting" style={{ background: 'rgba(15,23,42,0.85)', padding: '10px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }}>
              {activePlayer?.name} satın alma kararı veriyor...
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 3. Auction Bidding Panel
  const renderAuctionPanel = () => {
    const tile = getPropertyDetail(gameData.auction.tileId);
    if (!tile) return null;
    const currentBidderId = gameData.auction.activeBidders[gameData.auction.currentBidderIndex];
    const currentBidder = gameData.players.find(p => p.playerId === currentBidderId);
    const highestBidder = gameData.players.find(p => p.playerId === gameData.auction.highestBidderId);

    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 10001 }}>
        <div className="modal-backdrop"></div>
        <div className="stage-auction-panel animate-pop-in" style={{ zIndex: 10002 }}>
          <div className="stage-auction-header">
            <Gavel size={18} />
            <span>AÇIK ARTIRMA: {tile.name}</span>
          </div>
          <div className="stage-auction-stats">
            <div className="highest-bid">
              En Yüksek: <strong>₺{gameData.auction.highestBid}</strong>
              {highestBidder && <div className="bidder-name">Kimden: {highestBidder.name}</div>}
            </div>
            <div className="next-bidder">
              Sıra: <span style={{ color: currentBidder?.color }}>{currentBidder?.name}</span>
            </div>
          </div>

          <div className="stage-auction-bidders-list" style={{ margin: '14px 0', display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Katılımcı Bakiyeleri</div>
            {gameData.players.map(p => {
              const isActive = gameData.auction.activeBidders.includes(p.playerId);
              const isTurn = isActive && gameData.auction.activeBidders[gameData.auction.currentBidderIndex] === p.playerId;
              return (
                <div key={p.playerId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: isActive ? 1 : 0.45 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TokenIcon token={p.token} color={p.color} size={16} />
                    <span style={{ fontWeight: isTurn ? 800 : 500, color: isTurn ? '#fcd34d' : '#e2e8f0', fontSize: '0.85rem' }}>
                      {p.name} {isTurn && '⚡'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: isActive ? '#10b981' : '#ef4444' }}>
                    {isActive ? `₺${p.money}` : 'Çekildi ❌'}
                  </div>
                </div>
              );
            })}
          </div>

          {(isMyBidTurn || (gameData?.phase === 'AUCTION' && currentBidderId === myPlayerId)) ? (
            <div className="stage-auction-actions">
              <div className="quick-bids">
                <button className="quick-btn" onClick={() => sendCommand('PLACE_AUCTION_BID', { amount: gameData.auction.highestBid + 10 })}>+₺10</button>
                <button className="quick-btn" onClick={() => sendCommand('PLACE_AUCTION_BID', { amount: gameData.auction.highestBid + 50 })}>+₺50</button>
                <button className="quick-btn" onClick={() => sendCommand('PLACE_AUCTION_BID', { amount: gameData.auction.highestBid + 100 })}>+₺100</button>
              </div>
              <div className="custom-bid-row">
                <input 
                  type="number" 
                  placeholder={`Min: ₺${gameData.auction.highestBid + 1}`}
                  value={auctionBid}
                  onChange={(e) => setAuctionBid(e.target.value)}
                  className="custom-bid-input"
                />
                <button 
                  className="bid-submit-btn" 
                  onClick={() => {
                    const bidVal = parseInt(auctionBid);
                    if (isNaN(bidVal) || bidVal <= gameData.auction.highestBid) {
                      alert('Teklif en yüksek tekliften fazla olmalı!');
                      return;
                    }
                    sendCommand('PLACE_AUCTION_BID', { amount: bidVal });
                    setAuctionBid('');
                  }}
                >
                  Teklif Et
                </button>
              </div>
              <button className="pass-btn" onClick={() => sendCommand('PASS_AUCTION')}>Tekliften Çekil ❌</button>
            </div>
          ) : (
            <div className="stage-waiting-text">
              {currentBidder?.name} teklif veriyor...
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 4. Pending trade panel response
  const renderPendingTradePanel = () => {
    const pendingTrade = Array.isArray(gameData.pendingTrades)
      ? gameData.pendingTrades.find(t => t.receiverId === myPlayerId)
      : gameData.pendingTrade;

    if (!pendingTrade) return null;

    const sender = gameData.players.find(p => p.playerId === pendingTrade.senderId);
    const incomingCount = Array.isArray(gameData.pendingTrades)
      ? gameData.pendingTrades.filter(t => t.receiverId === myPlayerId).length
      : 1;

    // Compact Top Floating Bar when in Peek Mode
    if (isTradePeekMode) {
      return createPortal(
        <div style={{
          position: 'fixed',
          top: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 150000,
          background: 'rgba(15, 23, 42, 0.96)',
          border: '2px solid #fbbf24',
          borderRadius: '12px',
          padding: '10px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#fff',
          maxWidth: '94vw',
          flexWrap: 'wrap'
        }}>
          <div style={{ fontWeight: 800, color: '#fbbf24', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🤝 {t('trades.minimized_incoming', '📌 Gelen Takas Teklifi')}: {sender?.name}</span>
          </div>
          <button 
            onClick={() => setIsTradePeekMode(false)}
            style={{
              background: 'linear-gradient(135deg, #fbbf24, #d97706)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              color: '#0f172a',
              fontWeight: 800,
              fontSize: '0.82rem',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
            }}
          >
            {t('trades.return_to_deal', '📩 Teklife Dön')}
          </button>
          <button 
            className="trade-btn accept" 
            style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            onClick={() => sendCommand('ACCEPT_TRADE', { tradeId: pendingTrade.id })}
          >
            {t('trades.accept', 'Kabul Et')} 👍
          </button>
          <button 
            className="trade-btn reject" 
            style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            onClick={() => sendCommand('REJECT_TRADE', { tradeId: pendingTrade.id })}
          >
            {t('trades.reject', 'Reddet')} ❌
          </button>
        </div>,
        document.body
      );
    }

    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="modal-backdrop"></div>
        <div className="stage-trade-panel animate-pop-in" style={{ zIndex: 10002 }}>
          <div className="stage-trade-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <span>🤝 TAKAS TEKLİFİ GELDİ</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                onClick={() => setIsTradePeekMode(true)}
                style={{ background: 'rgba(255, 255, 255, 0.12)', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#e2e8f0', borderRadius: '6px', padding: '3px 8px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}
                title="Pencereyi simge durumuna küçültüp tahtayı inceleyin"
              >
                {t('trades.peek_board', '👁️ Tahtayı İncele')}
              </button>
              {incomingCount > 1 && (
                <button 
                  onClick={() => { setShowTradesModal(true); setTradesTab('incoming'); }}
                  style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid #fbbf24', color: '#fbbf24', borderRadius: '6px', padding: '3px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  Tüm Teklifler ({incomingCount}) ➔
                </button>
              )}
            </div>
          </div>
          <div className="trade-deal-grid">
            <div className="trade-column">
              <span className="owner-title">{sender?.name} Verecek:</span>
              <span className="deal-money">₺{pendingTrade.giveMoney}</span>
              <div className="deal-props">
                {(pendingTrade.giveProperties || [])
                  .map(id => getPropertyDetail(id))
                  .filter(Boolean)
                  .sort((a, b) => a.id - b.id)
                  .map(prop => (
                    <span 
                      key={prop.id} 
                      className="deal-prop-item"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPropertyId(prop.id);
                      }}
                      title="Tapu detayını ve konumunu göster"
                    >
                      <span className="deal-prop-bullet" style={{ backgroundColor: prop.color || '#475569' }}></span>
                      {prop.name} 🔍
                    </span>
                  ))
                }
                {pendingTrade.giveJailCards > 0 && (
                  <span className="deal-prop-item">
                    <span className="deal-prop-bullet" style={{ backgroundColor: '#eab308' }}></span>
                    🔑 {pendingTrade.giveJailCards}x Tahliye Kartı
                  </span>
                )}
              </div>
            </div>
            <div className="trade-direction">➔</div>
            <div className="trade-column">
              <span className="owner-title">Sizden İstenen:</span>
              <span className="deal-money">₺{pendingTrade.receiveMoney}</span>
              <div className="deal-props">
                {(pendingTrade.receiveProperties || [])
                  .map(id => getPropertyDetail(id))
                  .filter(Boolean)
                  .sort((a, b) => a.id - b.id)
                  .map(prop => (
                    <span 
                      key={prop.id} 
                      className="deal-prop-item"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPropertyId(prop.id);
                      }}
                      title="Tapu detayını ve konumunu göster"
                    >
                      <span className="deal-prop-bullet" style={{ backgroundColor: prop.color || '#475569' }}></span>
                      {prop.name} 🔍
                    </span>
                  ))
                }
                {pendingTrade.receiveJailCards > 0 && (
                  <span className="deal-prop-item">
                    <span className="deal-prop-bullet" style={{ backgroundColor: '#eab308' }}></span>
                    🔑 {pendingTrade.receiveJailCards}x Tahliye Kartı
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="trade-actions" style={{ display: 'flex', gap: '8px' }}>
            <button className="trade-btn accept" onClick={() => sendCommand('ACCEPT_TRADE', { tradeId: pendingTrade.id })}>{t('trades.accept', 'Kabul Et')} 👍</button>
            <button className="trade-btn counter" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', border: 'none', borderRadius: '8px', padding: '6px 12px', color: '#fff', fontWeight: 800, cursor: 'pointer' }} onClick={() => handleCounterOffer(pendingTrade)}>{t('trades.counter', 'Karşı Teklif')} 🔄</button>
            <button className="trade-btn reject" onClick={() => sendCommand('REJECT_TRADE', { tradeId: pendingTrade.id })}>{t('trades.reject', 'Reddet')} ❌</button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Dedicated "Teklifler" Multi-Trade Modal
  const renderTradesModal = () => {
    if (!showTradesModal || !gameData) return null;

    const pendingTradesList = Array.isArray(gameData.pendingTrades) 
      ? gameData.pendingTrades 
      : (gameData.pendingTrade ? [gameData.pendingTrade] : []);

    const incomingTrades = pendingTradesList.filter(t => t.receiverId === myPlayerId);
    const outgoingTrades = pendingTradesList.filter(t => t.senderId === myPlayerId);
    const activeList = tradesTab === 'incoming' ? incomingTrades : outgoingTrades;

    return createPortal(
      <div className="modal-overlay" onClick={() => setShowTradesModal(false)} style={{ zIndex: 160000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
        <div className="glass-panel" onClick={e => e.stopPropagation()} style={{ width: '92%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto', padding: '24px', background: 'rgba(15, 23, 42, 0.96)', border: '1.5px solid rgba(251, 191, 36, 0.4)', borderRadius: '16px', color: '#fff', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              onClick={() => { setShowTradesModal(false); setIsTradePeekMode(true); }} 
              style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', color: '#cbd5e1', borderRadius: '6px', padding: '3px 8px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
              title="Pencereyi kapatıp tahtayı inceleyin"
            >
              {t('trades.peek_board', '👁️ Tahtayı İncele')}
            </button>
            <button onClick={() => setShowTradesModal(false)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>
          
          <h2 style={{ margin: '0 0 16px 0', fontSize: '1.4rem', color: '#fcd34d', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🤝 {t('trades.title', 'Takas Teklifleri')}
          </h2>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
            <button 
              onClick={() => setTradesTab('incoming')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
                background: tradesTab === 'incoming' ? 'linear-gradient(135deg, #fbbf24, #d97706)' : 'rgba(255,255,255,0.08)',
                color: tradesTab === 'incoming' ? '#0f172a' : '#cbd5e1'
              }}
            >
              {t('trades.incoming', 'Gelen Teklifler')} ({incomingTrades.length})
            </button>
            <button 
              onClick={() => setTradesTab('outgoing')}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
                background: tradesTab === 'outgoing' ? 'linear-gradient(135deg, #fbbf24, #d97706)' : 'rgba(255,255,255,0.08)',
                color: tradesTab === 'outgoing' ? '#0f172a' : '#cbd5e1'
              }}
            >
              {t('trades.outgoing', 'Gönderilen Teklifler')} ({outgoingTrades.length})
            </button>
          </div>

          {activeList.length === 0 ? (
            <div style={{ padding: '30px 10px', textAlign: 'center', color: '#94a3b8', fontSize: '0.95rem' }}>
              {tradesTab === 'incoming' ? t('trades.no_incoming', 'Henüz gelen bir takas teklifi yok.') : t('trades.no_outgoing', 'Gönderdiğiniz aktif bir takas teklifi yok.')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {activeList.map((tr) => {
                const otherPlayerId = tradesTab === 'incoming' ? tr.senderId : tr.receiverId;
                const otherPlayer = gameData.players.find(p => p.playerId === otherPlayerId);
                const giveProps = (tr.giveProperties || []).map(id => propertiesData.find(pd => pd.id === id)).filter(Boolean);
                const receiveProps = (tr.receiveProperties || []).map(id => propertiesData.find(pd => pd.id === id)).filter(Boolean);

                return (
                  <div key={tr.id} style={{ background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '8px' }}>
                      <TokenIcon token={otherPlayer?.token} color={otherPlayer?.color} size={24} />
                      <span style={{ fontWeight: 800, color: otherPlayer?.color, fontSize: '0.95rem' }}>
                        {tradesTab === 'incoming' ? `${otherPlayer?.name} teklif gönderdi:` : `${otherPlayer?.name} oyuncusuna gönderilen teklif:`}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.82rem', marginBottom: '12px' }}>
                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '8px 10px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: '4px' }}>{t('trades.offers', 'Teklif Edilen')}:</div>
                        {tr.giveMoney > 0 && <div>💸 ₺{tr.giveMoney}</div>}
                        {giveProps.map(p => (
                          <div 
                            key={p.id} 
                            style={{ cursor: 'pointer', color: '#fbbf24', textDecoration: 'underline', marginTop: '2px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPropertyId(p.id);
                            }}
                            title="Tapu detayını ve konumunu göster"
                          >
                            🏠 {p.name} 🔍
                          </div>
                        ))}
                        {tr.giveJailCards > 0 && <div>🎴 {tr.giveJailCards}x Sürgün Kartı</div>}
                        {tr.giveMoney === 0 && giveProps.length === 0 && !tr.giveJailCards && <div style={{ color: '#64748b' }}>-</div>}
                      </div>

                      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '8px 10px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: 700, color: '#38bdf8', marginBottom: '4px' }}>{t('trades.requests', 'İstenen')}:</div>
                        {tr.receiveMoney > 0 && <div>💸 ₺{tr.receiveMoney}</div>}
                        {receiveProps.map(p => (
                          <div 
                            key={p.id} 
                            style={{ cursor: 'pointer', color: '#38bdf8', textDecoration: 'underline', marginTop: '2px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPropertyId(p.id);
                            }}
                            title="Tapu detayını ve konumunu göster"
                          >
                            🏠 {p.name} 🔍
                          </div>
                        ))}
                        {tr.receiveJailCards > 0 && <div>🎴 {tr.receiveJailCards}x Sürgün Kartı</div>}
                        {tr.receiveMoney === 0 && receiveProps.length === 0 && !tr.receiveJailCards && <div style={{ color: '#64748b' }}>-</div>}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {tradesTab === 'incoming' ? (
                        <>
                          <button
                            onClick={() => sendCommand('ACCEPT_TRADE', { tradeId: tr.id })}
                            style={{ background: 'linear-gradient(135deg, #22c55e, #15803d)', border: 'none', borderRadius: '8px', padding: '6px 14px', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {t('trades.accept', 'Kabul Et')} 👍
                          </button>
                          <button
                            onClick={() => handleCounterOffer(tr)}
                            style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', border: 'none', borderRadius: '8px', padding: '6px 14px', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {t('trades.counter', 'Karşı Teklif')} 🔄
                          </button>
                          <button
                            onClick={() => sendCommand('REJECT_TRADE', { tradeId: tr.id })}
                            style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', borderRadius: '8px', padding: '6px 14px', color: '#fca5a5', fontWeight: 700, cursor: 'pointer' }}
                          >
                            {t('trades.reject', 'Reddet')} ❌
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => sendCommand('CANCEL_TRADE', { tradeId: tr.id })}
                          style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', borderRadius: '8px', padding: '6px 14px', color: '#fca5a5', fontWeight: 700, cursor: 'pointer' }}
                        >
                          {t('trades.cancel', 'İptal Et')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 5. Debt resolution panel
  const renderDebtPanel = () => {
    const myProperties = me?.properties || [];
    const raiseMoneyTargets = myProperties.map(id => {
      const tile = getPropertyDetail(id);
      const propState = gameData.properties[id];
      if (!tile || !propState) return null;

      const hasHouses = propState.houses > 0 || propState.hotel;
      const canMortgage = !propState.mortgaged && !hasHouses;

      if (!hasHouses && !canMortgage) return null;

      return {
        tile,
        propState,
        hasHouses,
        canMortgage
      };
    }).filter(Boolean);

    return createPortal(
      <div className="debt-modal-overlay animate-fade-in" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        backgroundColor: 'rgba(15, 23, 42, 0.88)',
        backdropFilter: 'blur(12px)',
        zIndex: 10005,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}>
        <div className="stage-debt-panel animate-pop-in" style={{
          width: '100%',
          maxWidth: '480px',
          maxHeight: '85vh',
          overflowY: 'auto',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          border: '2.5px solid #ef4444',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 25px 60px rgba(239, 68, 68, 0.35)',
          boxSizing: 'border-box'
        }}>
          <div className="stage-debt-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ef4444', fontSize: '1.25rem', fontWeight: 900, marginBottom: '16px', fontFamily: 'Outfit, sans-serif' }}>
            <AlertTriangle size={24} />
            <span>🚨 İFLAS EŞİĞİ / BORÇ ÖDEME</span>
          </div>
          <div className="debt-details" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '1.05rem', color: '#fff', marginBottom: '6px' }}>Toplam Borç: <strong className="debt-red" style={{ color: '#fca5a5', fontSize: '1.2rem', fontWeight: 900 }}>₺{gameData.debt.amount}</strong></div>
            <div style={{ fontSize: '0.95rem', color: '#e2e8f0' }}>Cüzdanınız: <strong style={{ color: '#34d399', fontWeight: 800 }}>₺{me?.money}</strong></div>
            <div className="hint" style={{ color: '#fbbf24', marginTop: '10px', fontSize: '0.85rem', fontWeight: 600, lineHeight: '1.4' }}>
              💡 Borcunuzu ödemek için mülklerinizi ipotek edin veya tezgâhlarınızı satın.
            </div>
            {gameData.settings?.loanSharkMode && (
              <button 
                style={{ width: '100%', marginTop: '10px', background: 'linear-gradient(135deg, #d97706, #b45309)', border: '1px solid #f59e0b', borderRadius: '10px', padding: '10px', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                onClick={() => sendCommand('TAKE_LOAN_SHARK_LOAN')}
                disabled={(me?.activeLoans || []).length >= 3}
              >
                💸 Tefeciden Acil Borç Al (+₺2.000) {((me?.activeLoans || []).length > 0) ? `[${me.activeLoans.length}/3]` : ''}
              </button>
            )}
          </div>

          {raiseMoneyTargets.length > 0 && (
            <div className="debt-properties-list" style={{ marginTop: '14px', background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', maxHeight: '220px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#38bdf8', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚡ Para Kazanabileceğiniz Mülkler</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {raiseMoneyTargets.map(({ tile, propState, hasHouses, canMortgage }) => {
                  const houseValue = Math.floor(tile.houseCost / 2);
                  const mortgageValue = Math.floor(tile.price / 2);
                  return (
                    <div key={tile.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.04)', padding: '8px 10px', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: tile.color || '#fff', display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>{tile.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {hasHouses && (
                          <button 
                            className="quick-btn" 
                            style={{ fontSize: '0.75rem', padding: '6px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => sendCommand('SELL_HOUSE', { tileId: tile.id })}
                          >
                            Tezgâh Sat (+₺{houseValue})
                          </button>
                        )}
                        {canMortgage && (
                          <button 
                            className="quick-btn" 
                            style={{ fontSize: '0.75rem', padding: '6px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                            onClick={() => sendCommand('MORTGAGE_PROPERTY', { tileId: tile.id })}
                          >
                            İpotek (+₺{mortgageValue})
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {gameData.settings?.allowDebtTrade !== false && (
            <button 
              className="trade-propose-trigger-btn"
              style={{
                marginTop: '14px',
                width: '100%',
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                color: '#0f172a',
                fontWeight: 900,
                fontSize: '0.9rem',
                padding: '12px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(251, 191, 36, 0.3)'
              }}
              onClick={() => setIsProposingTrade(true)}
            >
              🤝 Diğer Oyuncularla Takas Yap (Nakit Sağla)
            </button>
          )}

          {gameData.settings?.allowDebtPropertySale !== false && gameData.debt.creditorId !== 'BANK' && (
            <div style={{ marginTop: '10px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', padding: '10px', borderRadius: '8px', fontSize: '0.8rem', color: '#fbbf24', textAlign: 'center', lineHeight: '1.4' }}>
              💡 Borç karşılığında alacaklıya mülk transfer etmek için takas seçeneğini kullanabilirsiniz.
            </div>
          )}

          <button 
            className="pass-btn" 
            style={{ 
              marginTop: '16px', 
              width: '100%', 
              padding: '12px', 
              fontSize: '0.9rem', 
              fontWeight: 800, 
              background: 'rgba(239, 68, 68, 0.2)', 
              border: '1.5px solid #ef4444', 
              color: '#fca5a5', 
              borderRadius: '10px', 
              cursor: 'pointer' 
            }} 
            onClick={() => {
              if (window.confirm('İflas ilan etmek istediğinize emin misiniz? Oyundan eleneceksiniz!')) {
                sendCommand('DECLARE_BANKRUPT');
              }
            }}
          >
            İflas İlan Et 💀
          </button>
        </div>
      </div>,
      document.body
    );
  };

  // 6. Game Over panel
  const renderGameOverPanel = () => {
    const getPlayerNetWorth = (p) => {
      if (!p || p.bankrupt) return 0;
      let propVal = 0;
      if (p.properties) {
        p.properties.forEach(pid => {
          const tile = propertiesData.find(b => b.id === pid);
          if (tile) {
            const state = gameData.properties?.[pid];
            if (state) {
              if (state.mortgaged) {
                propVal += Math.floor((tile.price || 100) / 2);
              } else {
                propVal += tile.price || 0;
                const totalHouses = (state.houses || 0) + (state.hotel ? 5 : 0);
                propVal += totalHouses * (tile.houseCost || 100);
              }
            } else {
              propVal += tile.price || 0;
            }
          }
        });
      }
      return (p.money || 0) + propVal;
    };

    const sortedPlayers = [...(gameData.players || [])].sort((a, b) => {
      if (a.bankrupt !== b.bankrupt) {
        return a.bankrupt ? 1 : -1;
      }
      return getPlayerNetWorth(b) - getPlayerNetWorth(a);
    });

    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 10001 }}>
        <div className="modal-backdrop"></div>
        <div className="stage-game-over-box animate-pop-in" style={{ 
          zIndex: 10002, 
          padding: '1.2rem', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '92%', 
          maxWidth: '460px', 
          maxHeight: '90vh',
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1.5px solid rgba(251, 191, 36, 0.35)',
          borderRadius: '16px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.8)',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: '900', color: '#fbbf24' }}>🏆 OYUN BİTTİ 🏆</h3>
          
          {gameData.winnerPlayerId && (
            <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1.05rem', color: '#e2e8f0' }}>
              Şampiyon: <strong style={{ color: '#34d399' }}>{gameData.players.find(p => p.playerId === gameData.winnerPlayerId)?.name}</strong>
            </h2>
          )}

          {/* Scrollable Container for Ranks & Awards */}
          <div style={{ 
            width: '100%', 
            overflowY: 'auto', 
            maxHeight: 'calc(90vh - 120px)', 
            paddingRight: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            boxSizing: 'border-box'
          }}>
            {/* 1. LEADERBOARD LIST */}
            <div style={{ width: '100%' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📊 Sıralama & Servet Dağılımı
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {sortedPlayers.map((p, idx) => {
                  const netWorth = getPlayerNetWorth(p);
                  const isWinner = p.playerId === gameData.winnerPlayerId;
                  const rankColors = ['#fbbf24', '#cbd5e1', '#d97706', '#94a3b8'];
                  const rankColor = idx < 3 ? rankColors[idx] : rankColors[3];
                  return (
                    <div 
                      key={p.playerId} 
                      style={{ 
                        background: isWinner ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.03)', 
                        border: isWinner ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                        padding: '8px 10px', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <span style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          background: rankColor, 
                          color: '#0f172a', 
                          fontSize: '0.75rem', 
                          fontWeight: 800, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {idx + 1}
                        </span>
                        <TokenIcon token={p.token} color={p.color} size={20} />
                        <span style={{ 
                          fontSize: '0.8rem', 
                          fontWeight: 700, 
                          color: isWinner ? '#34d399' : '#f8fafc',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }}>
                          {p.name} {p.playerId === myPlayerId && '(Sen)'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#fbbf24' }}>
                          ₺{netWorth}
                        </div>
                        <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                          Nakit: ₺{p.money} | Mülk: {p.properties?.length || 0}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 2. STATS BREAKDOWN */}
            <div style={{ width: '100%' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '4px' }}>
                📈 Oyun İstatistikleri
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {gameData.players.map(p => (
                  <div key={p.playerId} style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', padding: '6px 8px', borderRadius: '6px', fontSize: '0.7rem' }}>
                    <div style={{ fontWeight: 800, color: p.color, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TokenIcon token={p.token} color={p.color} size={14} />
                      {p.name}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', color: '#cbd5e1' }}>
                      <div>Kira Geliri: <strong>₺{p.stats?.rentCollected || 0}</strong></div>
                      <div>Açık Artırma: <strong>{p.stats?.auctionsWon || 0} Kazanılan</strong></div>
                      <div>İpotek Sayısı: <strong>{p.stats?.mortgageCount || 0}</strong></div>
                      <div>Gezilen Kare: <strong>{p.stats?.spacesTraveled || 0}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. END GAME AWARDS */}
            {gameData.endGameAwards && (
              <div className="end-game-awards-container" style={{ width: '100%' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '4px' }}>
                  🏅 Altın Boyoz Ödülleri
                </h4>
                <div className="end-game-awards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', width: '100%' }}>
                  {Object.entries(gameData.endGameAwards).map(([key, award]) => (
                    <div key={key} className="award-card" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '6px 8px', borderRadius: '6px', textAlign: 'left', boxSizing: 'border-box' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '4px' }}>🏆 {award.name}</div>
                      <div style={{ fontSize: '0.58rem', color: '#94a3b8', lineHeight: '1.2', marginTop: '2px' }}>{award.desc}</div>
                      <div style={{ fontSize: '0.65rem', marginTop: '4px', fontWeight: '700', color: '#34d399', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Ağa: {award.winner}</span>
                        {award.value && <span style={{ color: '#38bdf8', fontWeight: '800' }}>{award.value}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '0.75rem', width: '100%' }}>
            <button 
              className="gold-action-btn small pulse-glow" 
              style={{ flex: 1, padding: '10px 0', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              onClick={() => setShowStoryModal(true)}
            >
              <Share2 size={16} /> Story Kartı Paylaş
            </button>

            <button className="gold-action-btn small" style={{ flex: 1, padding: '10px 0', fontSize: '0.85rem', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => window.location.href = '/'}>
              Ana Menüye Dön
            </button>
          </div>

          {showStoryModal && (
            <StoryCardModal gameData={gameData} onClose={() => setShowStoryModal(false)} />
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 7. Initial Order Roll sequence
  const renderOrderRollPanel = () => {
    return (
      <div className="stage-action-box animate-pop-in">
        <div className="turn-alert-glow">BAŞLANGIÇ SIRA BELİRLEME</div>
        {gameData.orderRolls && gameData.orderRolls[myPlayerId] !== undefined ? (
          <div className="stage-info-box">
            <span>Zarınız: <strong>{gameData.orderRolls[myPlayerId]}</strong></span>
            <p>Diğer oyuncuların atması bekleniyor...</p>
          </div>
        ) : (
          <button className="gold-action-btn pulse-glow" onClick={() => sendCommand('ROLL_ORDER_DICE')}>
            Sıra Zarı At 🎲
          </button>
        )}
      </div>
    );
  };

  // 8. Jail Decision Option Buttons (Glassmorphism Portal)
  const renderJailDecisionPanel = () => {
    if (!isMyTurn && !showOthersCards) return null;
    return createPortal(
      <div className="modal-overlay" style={{ zIndex: 10001 }}>
        <div className="modal-backdrop"></div>
        <div className="glass-panel animate-pop-in" style={{
          zIndex: 10002,
          maxWidth: '440px',
          width: '90%',
          padding: '24px 28px',
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '2px solid rgba(239, 68, 68, 0.6)',
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.25), 0 15px 35px rgba(0,0,0,0.6)',
          borderRadius: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '4px' }}>🚨</div>
          <h3 style={{ margin: '0 0 6px 0', fontFamily: 'Outfit, sans-serif', color: '#f87171', fontSize: '1.4rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px' }}>
            ÇİĞLİ SÜRGÜNÜNDESİNİZ
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '20px', lineHeight: '1.4' }}>
            Hapishaneden çıkmak için zarlarda <strong>çift atmayı</strong> deneyebilir, <strong>₺50 kefaret</strong> ödeyebilir veya <strong>Tahliye Kartı</strong> kullanabilirsiniz.
          </p>

          {isMyTurn ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button 
                onClick={() => sendCommand('ROLL_DICE')} 
                disabled={isRolling}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#0f172a',
                  border: 'none',
                  padding: '12px 18px',
                  borderRadius: '12px',
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 900,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(245, 158, 11, 0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>🎲</span>
                <span>ÇİFT ZARI DENE (ÜCRETSİZ)</span>
              </button>

              <button 
                onClick={() => sendCommand('PAY_JAIL_FINE')} 
                disabled={me?.money < (gameData.settings?.jailFineAmount || 50)}
                style={{
                  background: me?.money >= 50 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'rgba(255,255,255,0.1)',
                  color: me?.money >= 50 ? '#ffffff' : '#64748b',
                  border: 'none',
                  padding: '12px 18px',
                  borderRadius: '12px',
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 900,
                  cursor: me?.money >= 50 ? 'pointer' : 'not-allowed',
                  boxShadow: me?.money >= 50 ? '0 4px 15px rgba(16, 185, 129, 0.35)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>💸</span>
                <span>KEFARET ÖDE (₺{gameData.settings?.jailFineAmount || 50})</span>
              </button>

              {me?.jailFreeCards && me.jailFreeCards.length > 0 && (
                <button 
                  onClick={() => sendCommand('USE_JAIL_CARD')}
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '12px 18px',
                    borderRadius: '12px',
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '0.95rem',
                    fontWeight: 900,
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>🔑</span>
                  <span>TAHLİYE KARTINI KULLAN ({me.jailFreeCards.length} Adet)</span>
                </button>
              )}
            </div>
          ) : (
            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '10px', color: '#fbbf24', fontSize: '0.85rem', fontWeight: 800 }}>
              {activePlayer?.name} Çiğli Sürgünü'nden çıkış kararını veriyor...
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // 9. Turn Start / Waiting for Roll
  const renderTurnStartPanel = () => {
    if (isMyTurn) {
      return (
        <div className="stage-action-box animate-pop-in">
          <div className="turn-alert-glow">SIRA SENDE</div>
          <button className="gold-action-btn pulse-glow" onClick={() => sendCommand('ROLL_DICE')} disabled={isRolling}>
            {autoRoll ? "ZARI AT 🎲 (Otomatik)" : "ZARI AT 🎲"}
          </button>
          <button className="trade-propose-trigger-btn" onClick={() => {
            setIsProposingTrade(true);
            sendCommand('START_TRADE_PROPOSAL');
          }}>
            Takas Öner 🤝
          </button>
          {gameData.settings?.loanSharkMode && (
            <button 
              className="trade-propose-trigger-btn"
              style={{ marginTop: '6px', background: 'linear-gradient(135deg, #d97706, #92400e)', border: '1px solid #f59e0b', color: '#fef3c7' }}
              onClick={() => sendCommand('TAKE_LOAN_SHARK_LOAN')}
              disabled={(me?.activeLoans || []).length >= 3}
              title="Tefeci Necmi'den ₺2.000 al. 4 tur boyunca her tur ₺1.000 taksit kesilir."
            >
              💸 Tefeciden Borç Al (₺2.000) {((me?.activeLoans || []).length > 0) ? `[${me.activeLoans.length}/3]` : ''}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="stage-info-box">
        <div className="pulse-dot"></div>
        <span>Sıra <strong>{activePlayer?.name}</strong> oyuncusunda</span>
        <p>Zar atması bekleniyor...</p>
      </div>
    );
  };

  // 10. Turn End / Optional Actions
  const renderTurnEndPanel = () => {
    if (isMyTurn) {
      const rolledDouble = gameData.dice?.isDouble && gameData.doublesRolledThisTurn > 0 && !me?.inJail;
      return (
        <div className="stage-action-box animate-pop-in">
          <button className="gold-action-btn end-turn-btn pulse-glow" onClick={() => sendCommand('END_TURN')}>
            {rolledDouble ? "ZARI TEKRAR AT 🎲 (Otomatik)" : "TURU BİTİR ⏹️ (Otomatik)"}
          </button>
          <button className="trade-propose-trigger-btn" onClick={() => {
            setIsProposingTrade(true);
            sendCommand('START_TRADE_PROPOSAL');
          }}>
            Takas Öner 🤝
          </button>
          {gameData.settings?.loanSharkMode && (
            <button 
              className="trade-propose-trigger-btn"
              style={{ marginTop: '6px', background: 'linear-gradient(135deg, #d97706, #92400e)', border: '1px solid #f59e0b', color: '#fef3c7' }}
              onClick={() => sendCommand('TAKE_LOAN_SHARK_LOAN')}
              disabled={(me?.activeLoans || []).length >= 3}
              title="Tefeci Necmi'den ₺2.000 al. 4 tur boyunca her tur ₺1.000 taksit kesilir."
            >
              💸 Tefeciden Borç Al (₺2.000) {((me?.activeLoans || []).length > 0) ? `[${me.activeLoans.length}/3]` : ''}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="stage-info-box">
        <div className="pulse-dot"></div>
        <span><strong>{activePlayer?.name}</strong> sırasını tamamlıyor...</span>
      </div>
    );
  };

  // Resolve the active action pane on the right column
  const getActiveActionPanel = () => {
    if (gameData.phase === 'WAITING_FOR_PROPERTY_DECISION' && gameData.pendingAction && gameData.pendingAction.type === 'BUY_OR_AUCTION') {
      return renderPropertyDecision();
    }
    if (gameData.phase === 'AUCTION' && gameData.auction) {
      return renderAuctionPanel();
    }
    if (gameData.pendingTrade && gameData.pendingTrade.receiverId === myPlayerId) {
      return renderPendingTradePanel();
    }
    if (isMyDebt) {
      return renderDebtPanel();
    }
    if (gameData.phase === 'GAME_OVER') {
      return renderGameOverPanel();
    }

    if (isRolling) return null;

    if (showDrawnCard && cardToRender) {
      return null;
    }
    if (gameData.phase === 'ORDER_ROLL') {
      return renderOrderRollPanel();
    }
    if (gameData.phase === 'WAITING_FOR_JAIL_DECISION') {
      return renderJailDecisionPanel();
    }
    if (gameData.phase === 'WAITING_FOR_ROLL' || gameData.phase === 'TURN_START') {
      return renderTurnStartPanel();
    }
    if (gameData.phase === 'OPTIONAL_ACTIONS' || gameData.phase === 'TURN_END' || gameData.phase === 'WAITING_FOR_TURN_END') {
      return renderTurnEndPanel();
    }
    return null;
  };

  return (
    <div className="board-center-stage" style={{
      backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.85)), url(${activeCenterImg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      {/* Title Watermark */}
      <div className="board-title-overlay">
        <h2>{cityBrand.subtitle.toUpperCase()}</h2>
        <h1>{lang === 'en' ? 'DEED WARS' : 'TAPU SAVAŞLARI'}</h1>
        <p className="board-slogan">{cityBrand.tagline}</p>
      </div>

      {/* Gulf of Izmir Map SVG Outline */}
      <div className="gulf-map-container">
        <svg viewBox="0 0 200 200" className="gulf-svg">
          <path 
            d="M 10,80 C 40,80 60,60 80,60 C 110,60 120,95 110,120 C 100,140 140,150 160,130 C 180,110 190,70 190,50" 
            fill="none" 
            stroke="#1d4ed8" 
            strokeWidth="3.5" 
            strokeDasharray="4 4"
            opacity="0.35"
          />
          <path 
            d="M 15,90 C 42,90 62,72 82,72 C 112,72 122,102 112,125" 
            fill="none" 
            stroke="#38bdf8" 
            strokeWidth="1.5" 
            opacity="0.25"
          />
        </svg>
      </div>

      {/* Inner Active Overlay Area (Split Columns or absolute full stage components) */}
      <div className="stage-content-wrapper">
        {isProposingTrade && renderTradeBuilder()}
        {selectedPropertyId !== null && !isProposingTrade && renderTileInspector(selectedPropertyId)}

        {!isProposingTrade && selectedPropertyId === null && (
          <div className="clean-center-stage">
            {/* CENTER ACTION AREA: Dice & Turn Decision Box */}
            <div className="center-stage-actions">
              {(isRolling || lastDice) && (
                <div className="dice-stage-overlay animate-pop-in">
                  <div className="dice-stage-title">
                    {isRolling ? 'ZARLAR ATILIYOR' : 'ZAR SONUCU'}
                  </div>
                  <div className="dice-stage-row">
                    <Dice value={lastDice ? lastDice.dice1 : 1} rolling={isRolling} />
                    {(!lastDice || lastDice.dice2 !== null) && (
                      <Dice value={lastDice ? lastDice.dice2 : 6} rolling={isRolling} />
                    )}
                  </div>
                  {lastDice && !isRolling && (
                    <div className="dice-stage-result">
                      Toplam: <span>{lastDice.total}</span>
                      {lastDice.isDouble && <span className="double-badge">ÇİFT!</span>}
                    </div>
                  )}
                </div>
              )}
              {getActiveActionPanel()}
            </div>
          </div>
        )}
      </div>
      
      {/* Announcement Log (INSIDE BLUE CENTER STAGE - No tile overlap) */}
      {latestLog && logVisible && (
        <div className="center-stage-log animate-fade-in" style={{
          padding: '6px 18px',
          fontSize: '0.85rem',
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(15, 23, 42, 0.92)',
          border: '1.5px solid rgba(251, 191, 36, 0.5)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          top: '12px'
        }}>
          {(() => {
            const cleanMsg = latestLog.replace(/\s*\[cmd:[^\]]+\]/g, '');
            let matchedPlayer = null;
            if (gameData && gameData.players) {
              for (const p of gameData.players) {
                if (cleanMsg.includes(p.name)) {
                  matchedPlayer = p;
                  break;
                }
              }
            }
            return (
              <>
                {matchedPlayer ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                    <TokenIcon token={matchedPlayer.token} color={matchedPlayer.color} size={22} />
                  </span>
                ) : (
                  <span style={{ color: '#fbbf24', fontSize: '1rem', flexShrink: 0 }}>📢</span>
                )}
                <span style={{
                  color: matchedPlayer ? (matchedPlayer.color || '#fbbf24') : '#fbbf24',
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  fontFamily: 'Outfit, sans-serif'
                }}>
                  {cleanMsg.startsWith('📢 ') ? cleanMsg.substring(2) : cleanMsg}
                </span>
              </>
            );
          })()}
        </div>
      )}

      {/* Floating Action Pill Buttons (INSIDE BLUE CENTER STAGE - Bottom corners) */}
      <div className="center-stage-pill-bar">
        {onOpenPlayers && (
          <button className="center-stage-pill-btn left" onClick={onOpenPlayers} title="Ağalar & Portföy">
            👑 Ağalar & Portföy
          </button>
        )}
        {(() => {
          const pendingTradesList = Array.isArray(gameData?.pendingTrades) 
            ? gameData.pendingTrades 
            : (gameData?.pendingTrade ? [gameData.pendingTrade] : []);
          const incomingCount = pendingTradesList.filter(t => t.receiverId === myPlayerId).length;

          return (
            <button 
              className="center-stage-pill-btn middle" 
              onClick={() => setShowTradesModal(true)} 
              title="Takas Teklifleri"
              style={{
                background: incomingCount > 0 ? 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)' : 'rgba(15, 23, 42, 0.85)',
                color: incomingCount > 0 ? '#0f172a' : '#fff',
                border: '1.5px solid rgba(251, 191, 36, 0.6)',
                fontWeight: 800
              }}
            >
              🤝 {t('game.trades_button', 'Teklifler')} {incomingCount > 0 && <span className="unread-badge">{incomingCount}</span>}
            </button>
          );
        })()}
        {onOpenChat && (
          <button className="center-stage-pill-btn right" onClick={onOpenChat} title="Sohbet & Duyurular">
            💬 Sohbet & Duyurular {unreadChatCount > 0 && <span className="unread-badge">{unreadChatCount}</span>}
          </button>
        )}
      </div>

      {/* Portals */}
      {showDrawnCard && cardToRender && renderCardReveal()}
      {renderTradesModal()}
    </div>
  );
}
